import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const DotNetInstall = makeShortcode("DotNetInstall");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <DotNetInstall mdxType="DotNetInstall" />
    <p>{`At the moment, ASP.NET Core is the only supported platform for the PostHog .NET SDK. However, we do have experimental support for other platforms mentioned later in this document.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`dotnet add package PostHog.AspNetCore
`}</code></pre>
    <p>{`In your `}<inlineCode parentName="p">{`Program.cs`}</inlineCode>{` (or `}<inlineCode parentName="p">{`Startup.cs`}</inlineCode>{` for ASP.NET Core 2.x) file, add the following code:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-csharp"
      }}>{`using PostHog;

var builder = WebApplication.CreateBuilder(args);

// Add PostHog to the dependency injection container as a singleton.
builder.AddPostHog();
`}</code></pre>
    <p>{`Make sure to configure PostHog with your project API key, instance address, and optional personal API key. For example, in `}<inlineCode parentName="p">{`appsettings.json`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "PostHog": {
    "ProjectApiKey": "<ph_project_api_key>",
    "HostUrl": "<ph_client_api_host>"
  }
}
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` If the host is not specified, the default host `}<inlineCode parentName="p">{`https://us.i.posthog.com`}</inlineCode>{` is used.`}</p>
    </blockquote>
    <p>{`Use a secrets manager to store your personal API key. For example, when developing locally you can use the `}<inlineCode parentName="p">{`UserSecrets`}</inlineCode>{` feature of the `}<inlineCode parentName="p">{`dotnet`}</inlineCode>{` CLI:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`dotnet user-secrets init
dotnet user-secrets set "PostHog:PersonalApiKey" "phx_..."
`}</code></pre>
    <p>{`You can find your project API key and instance address in the `}<a parentName="p" {...{
        "href": "https://app.posthog.com/project/settings"
      }}>{`project settings`}</a>{` page in PostHog.`}</p>
    <h2 {...{
      "id": "working-with-net-feature-management"
    }}>{`Working with .NET Feature Management`}</h2>
    <p><inlineCode parentName="p">{`PostHog.AspNetCore`}</inlineCode>{` supports `}<a parentName="p" {...{
        "href": "https://learn.microsoft.com/en-us/azure/azure-app-configuration/feature-management-dotnet-reference"
      }}>{`.NET Feature Management`}</a>{`. This enables you to use the `}{`<`}{`feature /`}{`>`}{` tag helper and the `}<inlineCode parentName="p">{`FeatureGateAttribute`}</inlineCode>{` in your ASP.NET Core applications to gate access to certain features using PostHog feature flags.`}</p>
    <p>{`To use feature flags with the .NET Feature Management library, you'll need to implement the `}<inlineCode parentName="p">{`IPostHogFeatureFlagContextProvider`}</inlineCode>{` interface. The quickest way to do that is to inherit from the `}<inlineCode parentName="p">{`PostHogFeatureFlagContextProvider`}</inlineCode>{` class and override the `}<inlineCode parentName="p">{`GetDistinctId`}</inlineCode>{` and `}<inlineCode parentName="p">{`GetFeatureFlagOptionsAsync`}</inlineCode>{` methods.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-csharp"
      }}>{`public class MyFeatureFlagContextProvider(IHttpContextAccessor httpContextAccessor)
    : PostHogFeatureFlagContextProvider
{
    protected override string? GetDistinctId()
        => httpContextAccessor.HttpContext?.User.Identity?.Name;
    
    protected override ValueTask<FeatureFlagOptions> GetFeatureFlagOptionsAsync()
    {
        // In a real app, you might get this information from a 
        // database or other source for the current user.
        return ValueTask.FromResult(
            new FeatureFlagOptions
            {
                PersonProperties = new Dictionary<string, object?>
                {
                    ["email"] = "some-test@example.com"
                },
                OnlyEvaluateLocally = true
            });
    }
}
`}</code></pre>
    <p>{`Then, register your implementation in `}<inlineCode parentName="p">{`Program.cs`}</inlineCode>{` (or `}<inlineCode parentName="p">{`Startup.cs`}</inlineCode>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-csharp"
      }}>{`var builder = WebApplication.CreateBuilder(args);
builder.AddPostHog(options => {
    options.UseFeatureManagement<MyFeatureFlagContextProvider>();
});
`}</code></pre>
    <p>{`With this in place, you can now use `}<inlineCode parentName="p">{`feature`}</inlineCode>{` tag helpers in your Razor views:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<feature name="awesome-new-feature">
    <p>This is the new feature!</p>
</feature>
<feature name="awesome-new-feature" negate="true">
    <p>Sorry, no awesome new feature for you.</p>
</feature>
`}</code></pre>
    <p>{`Multivariate feature flags are also supported:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<feature name="awesome-new-feature" value="variant-a">
    <p>This is the new feature variant A!</p>
</feature>
<feature name="awesome-new-feature" value="variant-b">
    <p>This is the new feature variant B!</p>
</feature>
`}</code></pre>
    <p>{`You can also use the `}<inlineCode parentName="p">{`FeatureGateAttribute`}</inlineCode>{` to gate access to controllers or actions:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-csharp"
      }}>{`[FeatureGate("awesome-new-feature")]
public class NewFeatureController : Controller
{
    public IActionResult Index()
    {
        return View();
    }
}
`}</code></pre>
    <h2 {...{
      "id": "experimental-support-for-other-platforms"
    }}>{`Experimental support for other platforms`}</h2>
    <p>{`The PostHog package is multi-targeted to support `}<inlineCode parentName="p">{`net8.0`}</inlineCode>{` and `}<inlineCode parentName="p">{`dotnetstandard2.1`}</inlineCode>{`. This means it can be used in a wider range of projects than just ASP.NET Core. However, this support is experimental and not officially supported.`}</p>
    <p>{`If you are using a different platform, you can install the `}<inlineCode parentName="p">{`PostHog`}</inlineCode>{` package instead of `}<inlineCode parentName="p">{`PostHog.AspNetCore`}</inlineCode>{`. This package does not depend on ASP.NET Core and can be used in any .NET project.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`dotnet add package PostHog
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`PostHogClient`}</inlineCode>{` class must be implemented as a singleton in your project. For `}<inlineCode parentName="p">{`PostHog.AspNetCore`}</inlineCode>{`, this is handled by the `}<inlineCode parentName="p">{`builder.AddPostHog();`}</inlineCode>{` method. For the `}<inlineCode parentName="p">{`PostHog`}</inlineCode>{` package, you can do the following if you're using dependency injection:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-csharp"
      }}>{`builder.Services.AddPostHog();
`}</code></pre>
    <p>{`If you're not using a `}<inlineCode parentName="p">{`builder`}</inlineCode>{` (such as in a console application), you can do the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-csharp"
      }}>{`using PostHog;

var services = new ServiceCollection();
services.AddPostHog();
var serviceProvider = services.BuildServiceProvider();
var posthog = serviceProvider.GetRequiredService<IPostHogClient>();
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`AddPostHog`}</inlineCode>{` methods accept an optional `}<inlineCode parentName="p">{`Action<PostHogOptions>`}</inlineCode>{` parameter that you can use to configure the client.`}</p>
    <p>{`If you're not using dependency injection, you can create a static instance of the `}<inlineCode parentName="p">{`PostHogClient`}</inlineCode>{` class and use that everywhere in your project:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-csharp"
      }}>{`using PostHog;

public static readonly PostHogClient PostHog = new(new PostHogOptions {
    ProjectApiKey = "<ph_project_api_key>",
    HostUrl = new Uri("<ph_client_api_host>"),
    PersonalApiKey = Environment.GetEnvironmentVariable(
      "PostHog__PersonalApiKey")
});
`}</code></pre>
    <h2 {...{
      "id": "debug-mode"
    }}>{`Debug mode`}</h2>
    <p>{`If you're not seeing the expected events being captured, the feature flags being evaluated, or the surveys being shown, you can enable debug mode to see what's happening. `}</p>
    <p>{`To see detailed logging, set the log level to `}<inlineCode parentName="p">{`Debug`}</inlineCode>{` or `}<inlineCode parentName="p">{`Trace`}</inlineCode>{` in `}<inlineCode parentName="p">{`appsettings.json`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "DetailedErrors": true,
  "Logging": {
    "LogLevel": {
      "Default": "Information",
      "Microsoft.AspNetCore": "Warning",
      "PostHog": "Trace"
    }
  },
  ...
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      